import { useMemo, useState } from "react";

import {
  criarDraftItem,
  extrairMensagemErroAPI,
  getComparableValue,
  validarCompetencias,
} from "../Componentes/TabelaNotasFiscaisExpansivel/utils";

export default function useTabelaNotasFiscaisExpansivel({
  rows,
  buscarCompetenciasNota,
  salvarCompetenciasNota,
  enqueueSnackbar,
}) {
  const [orderBy, setOrderBy] = useState("nota_fiscal");
  const [orderDirection, setOrderDirection] = useState("asc");
  const [expandedRowIds, setExpandedRowIds] = useState({});
  const [competenciasByRow, setCompetenciasByRow] = useState({});
  const [draftByRow, setDraftByRow] = useState({});
  const [loadingByRow, setLoadingByRow] = useState({});
  const [savingByRow, setSavingByRow] = useState({});
  const [errorsByRow, setErrorsByRow] = useState({});

  const sortedRows = useMemo(() => {
    const sorted = [...rows];

    sorted.sort((a, b) => {
      const valueA = getComparableValue(a, orderBy);
      const valueB = getComparableValue(b, orderBy);

      if (valueA < valueB) return orderDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return orderDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [rows, orderBy, orderDirection]);

  const handleRequestSort = (field) => {
    const isSameField = orderBy === field;
    if (isSameField) {
      setOrderDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setOrderBy(field);
    setOrderDirection("asc");
  };

  const handleToggleExpand = async (row) => {
    const rowId = row.id;
    const isExpanded = !!expandedRowIds[rowId];

    if (isExpanded) {
      setExpandedRowIds((prev) => ({ ...prev, [rowId]: false }));
      return;
    }

    setExpandedRowIds((prev) => ({ ...prev, [rowId]: true }));
    setErrorsByRow((prev) => ({ ...prev, [rowId]: "" }));

    if (competenciasByRow[rowId]) {
      setDraftByRow((prev) => ({
        ...prev,
        [rowId]: competenciasByRow[rowId].map(criarDraftItem),
      }));
      return;
    }

    setLoadingByRow((prev) => ({ ...prev, [rowId]: true }));
    try {
      const competencias = await buscarCompetenciasNota({
        codigoNota: row.codigo_nota,
        numeroNota: row.nota_fiscal,
      });

      setCompetenciasByRow((prev) => ({ ...prev, [rowId]: competencias }));
      setDraftByRow((prev) => ({
        ...prev,
        [rowId]: competencias.map(criarDraftItem),
      }));
    } catch (error) {
      setCompetenciasByRow((prev) => ({ ...prev, [rowId]: [] }));
      setDraftByRow((prev) => ({ ...prev, [rowId]: [] }));
      enqueueSnackbar("Nao foi possivel carregar as competencias da nota.", {
        variant: "error",
      });
    } finally {
      setLoadingByRow((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const handleAddCompetencia = (rowId) => {
    const draftAtual = draftByRow[rowId] ?? [];
    const novoDraft = [
      ...draftAtual,
      { mes_competencia: "", ano_competencia: "", porcentagem: "" },
    ];

    setDraftByRow((prev) => ({ ...prev, [rowId]: novoDraft }));
    setErrorsByRow((prev) => ({ ...prev, [rowId]: "" }));
  };

  const handleRemoveCompetencia = (rowId, index) => {
    const draftAtual = draftByRow[rowId] ?? [];
    const novoDraft = draftAtual.filter((_, i) => i !== index);

    setDraftByRow((prev) => ({ ...prev, [rowId]: novoDraft }));
    setErrorsByRow((prev) => ({ ...prev, [rowId]: "" }));
  };

  const handleChangeCompetencia = (rowId, index, field, value) => {
    let valorAtualizado = value;

    if (field === "porcentagem" && value !== "") {
      const valorNumerico = Number(String(value).replace(",", "."));
      if (!Number.isNaN(valorNumerico) && valorNumerico > 100) {
        valorAtualizado = "100";
      }
      if (!Number.isNaN(valorNumerico) && valorNumerico < 0) {
        valorAtualizado = "0";
      }
    }

    const draftAtual = draftByRow[rowId] ?? [];
    const novoDraft = draftAtual.map((item, i) =>
      i === index ? { ...item, [field]: valorAtualizado } : item
    );

    setDraftByRow((prev) => ({ ...prev, [rowId]: novoDraft }));
    setErrorsByRow((prev) => ({ ...prev, [rowId]: "" }));
  };

  const handleCancelarEdicao = (rowId) => {
    setDraftByRow((prev) => ({
      ...prev,
      [rowId]: (competenciasByRow[rowId] ?? []).map(criarDraftItem),
    }));
    setErrorsByRow((prev) => ({ ...prev, [rowId]: "" }));
  };

  const handleSalvarCompetencias = async (row) => {
    const rowId = row.id;
    const draft = (draftByRow[rowId] ?? []).map((item) => ({
      mes_competencia: String(item.mes_competencia || "").trim(),
      ano_competencia: String(item.ano_competencia || "").trim(),
      porcentagem:
        typeof item.porcentagem === "string"
          ? Number(item.porcentagem.replace(",", "."))
          : Number(item.porcentagem),
    }));

    const erroValidacao = validarCompetencias(draft);
    if (erroValidacao) {
      setErrorsByRow((prev) => ({ ...prev, [rowId]: erroValidacao }));
      return;
    }

    const competenciasPayload = draft.map((item) => ({
      competencia: `${item.mes_competencia}/${item.ano_competencia}`,
      porcentagem: item.porcentagem,
    }));

    setSavingByRow((prev) => ({ ...prev, [rowId]: true }));
    try {
      const persisted = await salvarCompetenciasNota({
        codigoNota: row.codigo_nota,
        numeroNota: row.nota_fiscal,
        competencias: competenciasPayload,
      });

      setCompetenciasByRow((prev) => ({ ...prev, [rowId]: persisted }));
      setDraftByRow((prev) => ({
        ...prev,
        [rowId]: persisted.map(criarDraftItem),
      }));
      setErrorsByRow((prev) => ({ ...prev, [rowId]: "" }));
      enqueueSnackbar("Competencias salvas com sucesso.", {
        variant: "success",
      });
    } catch (error) {
      const mensagemErro =
        extrairMensagemErroAPI(error) ||
        "Nao foi possivel salvar as competencias.";
      setErrorsByRow((prev) => ({ ...prev, [rowId]: mensagemErro }));
    } finally {
      setSavingByRow((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  return {
    orderBy,
    orderDirection,
    expandedRowIds,
    draftByRow,
    loadingByRow,
    savingByRow,
    errorsByRow,
    sortedRows,
    handleRequestSort,
    handleToggleExpand,
    handleAddCompetencia,
    handleRemoveCompetencia,
    handleChangeCompetencia,
    handleCancelarEdicao,
    handleSalvarCompetencias,
  };
}
